import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldAlert,
  ArrowRight,
  Activity,
  Radar,
  Cpu,
  Bell,
  Gauge,
  Waypoints,
  Database,
  Siren,
  ShieldCheck,
  Smartphone,
  Check,
  X,
  Menu,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { HeroRadar } from "@/components/HeroRadar";
import { Reveal } from "@/components/Reveal";
import { RiskGauge } from "@/components/RiskGauge";
import { RiskBadge } from "@/components/RiskBadge";
import { useSentinel } from "@/context/SentinelContext";
import { formatTimestamp } from "@/data/mockData";

const NAV_LINKS = [
  { href: "#capabilities", label: "Capabilities" },
  { href: "#how", label: "How it works" },
  { href: "#features", label: "Features" },
  { href: "#preview", label: "Dashboard" },
];

function Eyebrow({ children, center }) {
  return (
    <p className={`inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-300 ${center ? "mx-auto" : ""}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
      {children}
    </p>
  );
}

function MarketingNav() {
  const [open, setOpen] = useState(false);
  return (
    <header className="fixed inset-x-0 top-0 z-50 flex justify-center px-4 pt-4">
      <div className="flex w-full max-w-6xl items-center justify-between rounded-full border border-white/10 bg-sentinel-bg/70 px-3 py-2 pl-5 backdrop-blur-xl">
        <Link to="/" className="flex items-center gap-2.5" data-testid="landing-brand">
          <span className="flex h-7 w-7 items-center justify-center rounded-md border border-sentinel-border bg-sentinel-accent/10">
            <ShieldAlert className="h-4 w-4 text-sentinel-accent" strokeWidth={2.2} />
          </span>
          <span className="font-heading text-[14px] font-bold tracking-[0.24em] text-white">SENTINEL</span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href} className="text-sm text-slate-400 transition-colors hover:text-white" data-testid={`nav-${l.href.slice(1)}`}>
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          <Link to="/login" className="px-3 text-sm text-slate-300 transition-colors hover:text-white" data-testid="nav-signin">
            Sign in
          </Link>
          <Button asChild className="h-9 rounded-full bg-white px-4 font-semibold text-black hover:bg-slate-200">
            <Link to="/dashboard" data-testid="nav-launch-dashboard">Open Dashboard</Link>
          </Button>
        </div>

        <button className="rounded-full border border-white/10 p-2 text-slate-300 md:hidden" onClick={() => setOpen((o) => !o)} data-testid="landing-mobile-menu-toggle" aria-label="Toggle menu">
          {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>
      </div>

      {open && (
        <div className="absolute left-4 right-4 top-[68px] rounded-2xl border border-white/10 bg-sentinel-deep p-4 md:hidden">
          <nav className="flex flex-col gap-3">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-sm text-slate-300">{l.label}</a>
            ))}
            <div className="mt-2 flex flex-col gap-2">
              <Button asChild variant="outline" className="rounded-full border-sentinel-border bg-transparent text-slate-200"><Link to="/login">Sign in</Link></Button>
              <Button asChild className="rounded-full bg-white font-semibold text-black hover:bg-slate-200"><Link to="/dashboard">Open Dashboard</Link></Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

function Section({ id, className = "", children }) {
  return (
    <section id={id} className={`mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 ${className}`}>
      {children}
    </section>
  );
}

function SectionHead({ eyebrow, title, sub, center }) {
  return (
    <Reveal className={center ? "mx-auto max-w-2xl text-center" : "max-w-2xl"}>
      <Eyebrow center={center}>{eyebrow}</Eyebrow>
      <h2 className="mt-6 font-heading text-3xl font-semibold leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-[2.75rem]">{title}</h2>
      {sub && <p className="mt-5 text-base leading-relaxed text-slate-400">{sub}</p>}
    </Reveal>
  );
}

const TRUST = ["Realtime · Supabase", "Sensor fusion", "AI risk analysis", "0–100 risk scoring", "Guardian Mode"];

const CAPABILITIES = [
  { icon: Cpu, title: "AI Risk Detection", body: "Sensor patterns are analysed against baselines to separate routine activity from anomaly." },
  { icon: Radar, title: "Real-Time Sensor Intelligence", body: "Motion, orientation, audio and location are fused into live situational context." },
  { icon: Bell, title: "Incident Detection", body: "Abnormal events are captured as incidents with their sensor trace and timestamp." },
  { icon: ShieldCheck, title: "Guardian Mode", body: "One control arms or pauses continuous monitoring across the entire system." },
  { icon: Siren, title: "Emergency Response", body: "High-risk events open verification and escalate to contacts when needed." },
  { icon: Activity, title: "Live Safety Monitoring", body: "A continuous sensing field keeps your safety state current in the background." },
];

const PIPELINE = [
  { n: "01", icon: Radar, title: "Monitor", body: "Read smartphone sensor intelligence as a continuous stream." },
  { n: "02", icon: Activity, title: "Detect", body: "Surface deviations from the normal pattern as incidents." },
  { n: "03", icon: Cpu, title: "Analyze", body: "Score each incident 0–100 with an AI explanation." },
  { n: "04", icon: Waypoints, title: "Respond", body: "Log silently, verify, or escalate to emergency contacts." },
];

const FEATURES = [
  { icon: Smartphone, title: "Sensor Intelligence" },
  { icon: Cpu, title: "AI Risk Analysis" },
  { icon: Bell, title: "Incident Monitoring" },
  { icon: ShieldCheck, title: "Guardian Mode" },
  { icon: Siren, title: "Emergency Response" },
  { icon: Gauge, title: "Live Safety State" },
];

const COMPARE = [
  "Automatic anomaly detection",
  "Continuous background monitoring",
  "Sensor + AI situational context",
  "Calibrated 0–100 risk scoring",
  "Emergency escalation with context",
  "Auditable per-incident explanation",
];

export default function Landing() {
  const { latestIncident, live } = useSentinel();
  const hasLive = live && latestIncident;
  const score = hasLive ? latestIncident.score : 0;
  const level = hasLive ? latestIncident.level : "SAFE";

  return (
    <div className="min-h-screen overflow-x-hidden bg-sentinel-bg text-slate-200">
      <MarketingNav />

      {/* HERO — centered cinematic */}
      <div className="relative overflow-hidden pt-28 sm:pt-32">
        <div className="pointer-events-none absolute inset-0 grid-texture radial-fade opacity-40" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[640px] spotlight-duo" />
        <Section className="relative pb-16 pt-8 text-center">
          <div className="animate-fade-up">
            <Eyebrow center>Safety Intelligence Platform</Eyebrow>
            <h1 className="mx-auto mt-7 max-w-4xl font-heading text-5xl font-bold leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl">
              See danger before it becomes an emergency.
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
              AI-powered smartphone safety intelligence that detects abnormal
              situations and provides real-time risk intelligence.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button asChild className="group h-12 rounded-full bg-white px-6 font-semibold text-black hover:bg-slate-200">
                <Link to="/dashboard" data-testid="hero-primary-cta">
                  Open Safety Dashboard
                  <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-12 rounded-full border-white/10 bg-white/[0.02] px-6 text-slate-200 hover:border-white/20 hover:bg-white/[0.05] hover:text-white">
                <a href="#how" data-testid="hero-secondary-cta">See How Sentinel Works</a>
              </Button>
            </div>
          </div>

          <div className="animate-fade-up [animation-delay:140ms] relative mx-auto mt-16 max-w-3xl">
            <HeroRadar />
          </div>
        </Section>

        {/* TRUST STRIP */}
        <Section className="relative border-t border-white/5 py-6">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {TRUST.map((t) => (
              <span key={t} className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">{t}</span>
            ))}
          </div>
        </Section>
      </div>

      {/* CAPABILITIES */}
      <Section id="capabilities" className="border-t border-white/5 py-20 lg:py-28">
        <SectionHead center eyebrow="Capabilities" title="A complete safety intelligence stack." sub="The actual capabilities powering the Sentinel platform today." />
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {CAPABILITIES.map((c, i) => (
            <Reveal key={c.title} delay={(i % 3) * 80} className="group rounded-2xl border border-white/[0.07] bg-sentinel-card p-6 transition-colors hover:border-white/15 hover:bg-[#14151c]">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03]">
                <c.icon className="h-5 w-5 text-cyan-400 transition-transform group-hover:scale-110" strokeWidth={2} />
              </span>
              <h3 className="mt-5 text-base font-semibold text-white">{c.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{c.body}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section id="how" className="border-t border-white/5 py-20 lg:py-28">
        <SectionHead center eyebrow="How it works" title="Monitor. Detect. Analyze. Respond." sub="One continuous pipeline that turns raw sensor data into a decision the moment it matters." />
        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
          {PIPELINE.map((step, i) => (
            <Reveal key={step.title} delay={i * 90} className="group relative rounded-2xl border border-white/[0.07] bg-sentinel-card p-7 transition-colors hover:border-white/15">
              <span className="font-mono text-sm font-semibold text-cyan-400">{step.n}</span>
              <span className="mt-4 flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03]">
                <step.icon className="h-5 w-5 text-white" strokeWidth={1.8} />
              </span>
              <h3 className="mt-5 font-heading text-lg font-medium text-white">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{step.body}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* REAL-TIME RISK INTELLIGENCE — large split with LIVE data */}
      <Section id="risk" className="border-t border-white/5 py-20 lg:py-28">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
          <SectionHead
            eyebrow="Real-Time Risk Intelligence"
            title="Every situation, resolved to a single live score."
            sub="Sentinel expresses risk as a continuous 0–100 score mapped to clear semantic bands, streamed live from Supabase. Colour communicates state — never decoration."
          />
          <Reveal delay={120} className="relative overflow-hidden rounded-3xl border border-white/[0.07] bg-sentinel-deep p-8 glow-soft">
            <div className="pointer-events-none absolute inset-0 spotlight-cyan" />
            <div className="relative flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:gap-8">
              <RiskGauge score={score} size={188} />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {hasLive ? (
                    <RiskBadge level={level} label={latestIncident.rawLevel} />
                  ) : (
                    <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Awaiting live data</span>
                  )}
                </div>
                <p className="mt-4 flex flex-wrap items-center gap-2">
                  <RiskBadge level="SAFE" showDot={false} /><RiskBadge level="LOW" showDot={false} /><RiskBadge level="MEDIUM" showDot={false} /><RiskBadge level="HIGH" showDot={false} />
                </p>
                <p className="mt-4 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  {hasLive ? `Live · Supabase · ${formatTimestamp(latestIncident.timestamp)}` : "Live · Supabase"}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* FEATURES — bento */}
      <Section id="features" className="border-t border-white/5 py-20 lg:py-28">
        <SectionHead center eyebrow="Sentinel Features" title="Engineered for the critical moment." />
        <div className="mt-14 grid grid-cols-2 gap-5 sm:grid-cols-3">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={(i % 3) * 70} className="group flex flex-col items-center justify-center rounded-2xl border border-white/[0.07] bg-sentinel-card px-4 py-10 text-center transition-colors hover:border-white/15 hover:bg-[#14151c]">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03]">
                <f.icon className="h-5 w-5 text-cyan-400 transition-transform group-hover:scale-110" strokeWidth={2} />
              </span>
              <h3 className="mt-4 text-sm font-semibold text-white">{f.title}</h3>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* COMPARISON */}
      <Section className="border-t border-white/5 py-20 lg:py-28">
        <SectionHead center eyebrow="Why Sentinel" title="Beyond a manual panic button." sub="Traditional safety apps depend on you reacting. Sentinel watches continuously and acts on context." />
        <div className="mx-auto mt-14 grid max-w-4xl grid-cols-1 gap-5 md:grid-cols-2">
          <Reveal className="rounded-3xl border border-white/[0.07] bg-sentinel-card p-8">
            <h3 className="font-heading text-lg font-medium text-slate-300">Manual safety apps</h3>
            <ul className="mt-6 space-y-4">
              {COMPARE.map((c) => (
                <li key={c} className="flex items-start gap-3 text-sm text-slate-500">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-slate-600" /> <span className="line-through decoration-slate-700">{c}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={100} className="relative overflow-hidden rounded-3xl border border-sentinel-accent/30 bg-sentinel-card p-8 glow-accent">
            <div className="pointer-events-none absolute inset-0 spotlight-accent" />
            <h3 className="relative font-heading text-lg font-medium text-white">Sentinel</h3>
            <ul className="relative mt-6 space-y-4">
              {COMPARE.map((c) => (
                <li key={c} className="flex items-start gap-3 text-sm text-slate-200">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" /> {c}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </Section>

      {/* LIVE DASHBOARD PREVIEW — real data */}
      <Section id="preview" className="border-t border-white/5 py-20 lg:py-28">
        <SectionHead center eyebrow="Live Dashboard" title="Your safety state, at a glance." sub="A preview of the operational dashboard — streaming real incidents live from Supabase." />
        <Reveal delay={120} className="mt-14 overflow-hidden rounded-3xl border border-white/[0.07] bg-sentinel-deep glow-soft">
          <div className="flex items-center gap-2 border-b border-white/5 px-4 py-3">
            <span className="h-2.5 w-2.5 rounded-full bg-white/10" /><span className="h-2.5 w-2.5 rounded-full bg-white/10" /><span className="h-2.5 w-2.5 rounded-full bg-white/10" />
            <span className="ml-3 font-mono text-[11px] text-slate-600">sentinel · operational dashboard</span>
            <span className="ml-auto flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-emerald-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Live · Supabase
            </span>
          </div>
          <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-3">
            <div className="flex flex-col items-center justify-center rounded-2xl border border-white/5 bg-sentinel-card p-6">
              <RiskGauge score={score} size={168} />
            </div>
            <div className="flex flex-col justify-center gap-3 rounded-2xl border border-white/5 bg-sentinel-card p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">Current Safety State</p>
              {hasLive ? <RiskBadge level={level} label={latestIncident.rawLevel} className="w-fit" /> : <span className="font-mono text-xs text-slate-500">Awaiting live data</span>}
              <p className="text-sm text-slate-400">Current safety state and risk score, updated live from Supabase.</p>
            </div>
            <div className="flex flex-col justify-center gap-3 rounded-2xl border border-white/5 bg-sentinel-card p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">Sensor Values</p>
              {hasLive && latestIncident.sensors?.length ? (
                <div className="grid grid-cols-3 gap-2">
                  {latestIncident.sensors.map((s) => (
                    <div key={s.key || s.sensor} className="rounded-lg border border-white/5 bg-sentinel-deep px-2 py-2 text-center">
                      <p className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{s.sensor}</p>
                      <p className="font-mono text-sm font-semibold text-slate-100">{s.reading}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400">Live sensor readings from the latest incident.</p>
              )}
            </div>
          </div>
        </Reveal>
        <div className="mt-8 text-center">
          <Button asChild className="h-11 rounded-full bg-white px-6 font-semibold text-black hover:bg-slate-200">
            <Link to="/dashboard" data-testid="preview-open-dashboard">
              Open the dashboard <ArrowRight className="ml-1.5 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </Section>

      {/* INCIDENT RESPONSE + EMERGENCY COMMAND CENTER */}
      <Section id="emergency" className="border-t border-white/5 py-20 lg:py-28">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
          <SectionHead
            eyebrow="Incident Response · Command Center"
            title="From signal to escalation, in one flow."
            sub="Every abnormal event becomes a documented incident. When risk crosses the high threshold, Sentinel opens a verification window and escalates to your emergency contacts with full context."
          />
          <Reveal delay={120} className="relative overflow-hidden rounded-3xl border border-red-500/40 bg-[#130b0d] glow-danger">
            <div className="pointer-events-none absolute inset-0 spotlight-danger" />
            <div className="relative flex items-center justify-between border-b border-red-500/25 px-5 py-3">
              <span className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-red-400">
                <span className="h-2 w-2 animate-pulse-ring rounded-full bg-red-500" /> High Risk Detected
              </span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-red-400/60">Example</span>
            </div>
            <div className="relative p-7">
              <div className="flex items-end justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">Risk Score</p>
                  <p className="mt-1 font-heading text-6xl font-bold leading-none text-red-400">91<span className="text-2xl text-slate-600">/100</span></p>
                </div>
                <RiskBadge level="HIGH" />
              </div>
              <div className="mt-7 space-y-3.5 border-t border-red-500/15 pt-6">
                <Row label="AI recommendation" value="Verify user safety" valueClass="text-slate-200" />
                <Row label="Verification" value="Awaiting response" valueClass="text-amber-400" />
                <Row label="Escalation" value="ACTIVE" valueClass="text-red-400" />
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section className="border-t border-white/5 py-20 lg:py-28">
        <Reveal className="relative overflow-hidden rounded-[2rem] border border-white/[0.07] bg-sentinel-deep px-6 py-20 text-center sm:px-12">
          <div className="pointer-events-none absolute inset-0 grid-texture opacity-25" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-72 spotlight-duo" />
          <div className="relative mx-auto max-w-2xl">
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Safety intelligence starts with awareness.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-400">Monitor signals. Detect danger. Respond faster.</p>
            <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
              <Button asChild className="group h-12 rounded-full bg-white px-8 font-semibold text-black hover:bg-slate-200">
                <Link to="/dashboard" data-testid="final-cta-dashboard">
                  Enter Sentinel
                  <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-12 rounded-full border-white/10 bg-white/[0.02] px-8 text-slate-200 hover:border-white/20 hover:bg-white/[0.05] hover:text-white">
                <Link to="/login" data-testid="final-cta-signin">Sign in</Link>
              </Button>
            </div>
          </div>
        </Reveal>
      </Section>

      {/* FOOTER — multi-column */}
      <footer className="border-t border-white/5">
        <Section className="grid grid-cols-2 gap-10 py-16 sm:grid-cols-4">
          <div className="col-span-2 sm:col-span-1">
            <div className="flex items-center gap-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-md border border-sentinel-border bg-sentinel-accent/10">
                <ShieldAlert className="h-4 w-4 text-sentinel-accent" strokeWidth={2.2} />
              </span>
              <span className="font-heading text-sm font-bold tracking-[0.24em] text-white">SENTINEL</span>
            </div>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-500">
              AI-powered smartphone safety intelligence for the moments that matter.
            </p>
          </div>
          <FooterCol title="Platform" links={[["Capabilities", "#capabilities"], ["How it works", "#how"], ["Features", "#features"], ["Dashboard", "#preview"]]} />
          <FooterCol title="Product" links={[["Open Dashboard", "/dashboard", true], ["Incidents", "/dashboard", true], ["Emergency", "/dashboard", true], ["Sign in", "/login", true]]} />
          <FooterCol title="Technology" links={[["Sensor fusion", "#risk"], ["AI risk analysis", "#risk"], ["Realtime Supabase", "#preview"], ["Guardian Mode", "#features"]]} />
        </Section>
        <Section className="flex flex-col items-start justify-between gap-4 border-t border-white/5 py-6 sm:flex-row sm:items-center">
          <p className="font-mono text-[11px] text-slate-600">© 2026 Sentinel Systems</p>
          <p className="font-mono text-[11px] text-slate-600">Built for safety intelligence</p>
        </Section>
      </footer>
    </div>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{title}</p>
      <ul className="mt-4 space-y-2.5">
        {links.map(([label, href, route]) => (
          <li key={label}>
            {route ? (
              <Link to={href} className="text-sm text-slate-400 transition-colors hover:text-white">{label}</Link>
            ) : (
              <a href={href} className="text-sm text-slate-400 transition-colors hover:text-white">{label}</a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Row({ label, value, valueClass = "text-slate-200" }) {
  return (
    <div className="flex items-center justify-between">
      <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">{label}</span>
      <span className={`font-mono text-sm font-semibold ${valueClass}`}>{value}</span>
    </div>
  );
}
